import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LinkBadge } from '../../../../components/common/LinkBadge';
import profileIcon from '../../../../assets/images/profile.png';
import verifiedIcon from '../../../../assets/icons/verified.png';

const formatDOB = (dob) => {
  if (!dob) return '-';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '-';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  const today = new Date();
  let age = today.getFullYear() - yyyy;
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return `${mm}/${dd}/${yyyy} (${age})`;
};

export const PlayerInfoView = ({ playerData }) => {
  const navigate = useNavigate();
  // The team logo opens that team's page
  const openTeam = () => playerData.team?._id && navigate(`/coach/team/${playerData.team._id}`);
  const schoolName = playerData.team && typeof playerData.team === 'object' ? playerData.team.name : (playerData.college !== '-' ? playerData.college : '');
  const [showTooltip] = useState(false);

  const pillClass = "w-full px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 rounded-full bg-transparent border-2 border-jp-ink/30 text-jp-ink text-[10px] sm:text-[11px] md:text-xs text-center font-medium truncate overflow-hidden whitespace-nowrap";
  const labelClass = "block text-jp-ink text-[8px] sm:text-[9px] uppercase tracking-widest mb-1.5 font-bold truncate";
  return (
    <div className="relative bg-transparent rounded-2xl pt-4 border-teal-700/50">
      {/* Header Section with Player Info and Team Logo */}
      <div className="relative bg-jp-card rounded-2xl p-4 sm:p-6 border border-jp-line overflow-hidden mb-6">
        {/* Background gradient accent */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10"></div>

        {/* Mobile Layout - Stack vertically */}
        <div className="relative block md:hidden space-y-4">
          {/* Top Row - Photo and Name */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 relative">
              <img
                src={playerData.photo || profileIcon}
                alt={playerData.name}
                className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl border-3 border-cyan-500/50 object-cover shadow-xl"
                onError={(e) => {
                  e.target.src = profileIcon;
                }}
              />
              {/* Verified Badge on top-right of photo - Half in, half out */}
              {playerData.registrationStatus === 'approved' && (
                <div className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 z-10" title="Verified Profile">
                  <img src={verifiedIcon} alt="Verified" className="w-10 h-10 sm:w-12 sm:h-12" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-1">
                <h1 className="text-lg sm:text-xl font-bold text-jp-ink text-left">
                  {playerData.name}
                </h1>
              </div>
              {/* School, then jersey number to its right */}
              {(schoolName || playerData.jerseyNumber) && (
                <div className="flex items-baseline gap-2 text-sm sm:text-base text-left">
                  {schoolName && (
                    <span className="text-jp-muted font-medium truncate">{schoolName}</span>
                  )}
                  {playerData.jerseyNumber && (
                    <span className="font-bold text-jp-muted flex-shrink-0">#{playerData.jerseyNumber}</span>
                  )}
                </div>
              )}
              <div className="text-xs sm:text-sm text-jp-muted mt-1 text-left">
                <span className="font-medium text-jp-muted">NCAA ID:</span> {playerData.ncaaId || '-'}
              </div>
              {/* Social Media Icons - Mobile */}
              {playerData.registrationStatus === 'approved' && (playerData.instaURL || playerData.xURL) && (
                <div className="flex items-center gap-2 mt-2">
                  {playerData.instaURL && (
                    <a
                      href={playerData.instaURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-7 h-7 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                      title="Instagram"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                  )}
                  {playerData.xURL && (
                    <a
                      href={playerData.xURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-7 h-7 bg-black rounded-full flex items-center justify-center hover:opacity-80 transition-opacity border border-jp-line"
                      title="X (Twitter)"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Contact Info Row */}
          {playerData.registrationStatus === 'approved' && (
            <div className="flex flex-col gap-2">
              {playerData.phoneNumber && (
                <a
                  href={`tel:${playerData.phoneNumber}`}
                  className="flex items-center gap-2 text-jp-muted hover:text-jp-accent-ink transition-colors"
                >
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <span className="text-xs sm:text-sm truncate">{playerData.phoneNumber}</span>
                </a>
              )}
              {playerData.email && (
                <a
                  href={`mailto:${playerData.email}`}
                  className="flex items-center gap-2 text-jp-muted hover:text-jp-accent-ink transition-colors"
                >
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <span className="text-xs sm:text-sm truncate">{playerData.email}</span>
                </a>
              )}
            </div>
          )}

          {/* Team Logo Row */}
          {playerData.team && typeof playerData.team === 'object' && playerData.team.logo && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={openTeam}
                aria-label={`View ${playerData.team.name || 'team'} team page`}
                className="group relative transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-2xl"
              >
                <img
                  src={playerData.team.logo}
                  alt={playerData.team.name || 'Team Logo'}
                  className="w-24 h-24 rounded-2xl border-3 border-cyan-500/50 object-contain shadow-xl p-2"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <LinkBadge className="-bottom-2 -right-2" />
              </button>
            </div>
          )}
        </div>

        {/* Desktop Layout - Side by side */}
        <div className="relative hidden md:flex items-center justify-between gap-4 lg:gap-6">
          {/* Left Side - Player Photo with Badge */}
          <div className="flex-shrink-0 relative">
            <img
              src={playerData.photo || profileIcon}
              alt={playerData.name}
              className="w-36 h-36 lg:w-44 lg:h-44 rounded-2xl border-3 border-cyan-500/50 object-cover shadow-xl"
              onError={(e) => {
                e.target.src = profileIcon;
              }}
            />
            {/* Verified Badge on top-right of photo - Half in, half out */}
            {playerData.registrationStatus === 'approved' && (
              <div className="absolute top-1 right-1 translate-x-1/2 -translate-y-1/2 z-10" title="Verified Profile">
                <img src={verifiedIcon} alt="Verified" className="w-12 h-12 lg:w-12 lg:h-12" />
              </div>
            )}
          </div>

          {/* Center - Player Info */}
          <div className="flex-1 min-w-0">
            {/* Player Name */}
            <div className="mb-1">
              <h1 className="text-2xl lg:text-3xl font-bold text-jp-ink truncate text-left">
                {playerData.name}
              </h1>
            </div>  

            {/* Jersey Number */}
            <div className="flex items-center gap-3 text-jp-muted">
              {/* School, then jersey number to its right */}
              {(schoolName || playerData.jerseyNumber) && (
                <div className="flex items-baseline gap-2 text-base lg:text-lg text-left">
                  {schoolName && (
                    <span className="text-jp-muted font-medium truncate">{schoolName}</span>
                  )}
                  {playerData.jerseyNumber && (
                    <span className="font-bold text-jp-muted flex-shrink-0">#{playerData.jerseyNumber}</span>
                  )}
                </div>
              )}
            </div>

            {/* NCAA ID */}
            <div className="text-sm text-jp-muted mt-1 text-left">
              <span className="font-medium text-jp-muted">NCAA ID:</span> {playerData.ncaaId || '-'}
            </div>

            {/* Social Media Icons - Desktop */}
            {playerData.registrationStatus === 'approved' && (playerData.instaURL || playerData.xURL) && (
              <div className="flex items-center gap-2 mt-2">
                {playerData.instaURL && (
                  <a
                    href={playerData.instaURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                    title="Instagram"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
                {playerData.xURL && (
                  <a
                    href={playerData.xURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 bg-black rounded-full flex items-center justify-center hover:opacity-80 transition-opacity border border-jp-line"
                    title="X (Twitter)"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Right Side - Phone, Email & Team Logo */}
          <div className="flex items-center gap-4 lg:gap-6">
            {/* Phone and Email - Only show when profile is approved/claimed */}
            {playerData.registrationStatus === 'approved' && (
              <div className="flex flex-col gap-2 text-sm">
                {playerData.phoneNumber && (
                  <a
                    href={`tel:${playerData.phoneNumber}`}
                    className="flex items-center gap-2 text-jp-muted hover:text-jp-accent-ink transition-colors"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <span className="text-sm">{playerData.phoneNumber}</span>
                  </a>
                )}
                {playerData.email && (
                  <a
                    href={`mailto:${playerData.email}`}
                    className="flex items-center gap-2 text-jp-muted hover:text-jp-accent-ink transition-colors"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <span className="text-sm">{playerData.email}</span>
                  </a>
                )}
              </div>
            )}

            {/* Team Logo */}
            {playerData.team && typeof playerData.team === 'object' && playerData.team.logo && (
              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={openTeam}
                  aria-label={`View ${playerData.team.name || 'team'} team page`}
                  className="group relative transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-2xl"
                >
                  <img
                    src={playerData.team.logo}
                    alt={playerData.team.name || 'Team Logo'}
                    className="w-28 h-28 lg:w-32 lg:h-32 rounded-2xl border-3 border-cyan-500/50 object-contain shadow-xl p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <LinkBadge className="-bottom-2 -right-2" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Grid Container */}
      <div className="grid grid-cols-1 gap-6 items-start">
        {/* Left Side - Info Fields */}
        <div className="space-y-4 w-full overflow-hidden">
          {/* First Row - Responsive columns */}
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="min-w-0">
              <label className={labelClass}>Date of Birth</label>
              <div className={pillClass}>{formatDOB(playerData.dob || playerData.dateOfBirth)}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>Class</label>
              <div className={pillClass}>{playerData.class}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>B/T</label>
              <div className={pillClass}>{playerData.bt}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>Position</label>
              <div className={pillClass}>{playerData.position}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>Hometown</label>
              <div className={pillClass}>{playerData.hometown}</div>
            </div>
          </div>

          {/* Second Row - Responsive columns */}
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="min-w-0">
              <label className={labelClass}>Commitment</label>
              <div className={pillClass}>{playerData.commitment === 'committed' && playerData.committedTo && playerData.committedTo !== '-' ? playerData.committedTo : 'Uncommitted'}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>High School</label>
              <div className={pillClass}>{playerData.highSchool}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>College</label>
              <div className={pillClass}>{playerData.team && typeof playerData.team === 'object' ? playerData.team.name : (playerData.college || '-')}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>Height</label>
              <div className={pillClass}>{playerData.height}</div>
            </div>
            <div className="min-w-0">
              <label className={labelClass}>Weight</label>
              <div className={pillClass}>{playerData.weight}</div>
            </div>
          </div>
        </div>

        {/* Right Side - Eligibility Section */}
        {/* <div className="flex flex-col items-start lg:items-end">
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-2 mb-2">
              <label className={labelClass}>Eligibility</label>
              <div className="relative">
                <button
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  className="text-jp-muted hover:text-jp-ink transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-jp-ink">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" strokeWidth="2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </button>

                {showTooltip && (
                  <div className="z-[9999] absolute left-full top-1/2 -translate-y-1/2 ml-2 w-36 bg-jp-section text-jp-ink text-[10px] rounded-lg p-2 shadow-xl border border-jp-line">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-black border border-gray-600"></div>
                        <span>4 regular years</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>Redshirt year</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                        <span>Medical redshirt</span>
                      </div>
                    </div>
                    <div className="absolute right-full top-1/2 -translate-y-1/2 mr-[-4px]">
                      <div className="border-4 border-transparent border-r-jp-section"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

           
            <div className="bg-jp-section rounded-2xl px-4 sm:px-5 py-3 sm:py-3.5">
              <div className="flex flex-col gap-2.5">
                <div className="flex gap-2.5">
                  {[1, 2, 3, 4].map((year) => (
                    <div
                      key={year}
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 ${
                        playerData.eligibilityYears?.includes(year.toString())
                          ? year === 1
                            ? 'bg-black border-blue-500'
                            : 'bg-blue-500 border-blue-500'
                          : 'bg-transparent border-blue-400/40'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex gap-2.5">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-green-500 bg-transparent" />
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-pink-500 bg-transparent" />
                </div>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default PlayerInfoView;
